import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TransactionSummary } from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api, ApiRequestError } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const ledgerUnmappedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/unmapped',
  component: LedgerUnmappedPage,
});

type UnmappedSortKey = 'date' | 'account' | 'direction' | 'amount' | 'category' | 'description';
const EMPTY_UNMAPPED: TransactionSummary[] = [];

function LedgerUnmappedPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  });
  const costCentersQuery = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.listCostCenters(),
  });
  const listQuery = useQuery({
    queryKey: ['unmapped'],
    queryFn: () => api.listUnmapped({ pageSize: 200 }),
  });

  const accountById = React.useMemo(
    () => new Map((accountsQuery.data ?? []).map((a) => [a.id, a])),
    [accountsQuery.data],
  );
  const overheadId = React.useMemo(
    () => (costCentersQuery.data ?? []).find((c) => c.isDefaultOverhead && c.active)?.id ?? '',
    [costCentersQuery.data],
  );
  const sortAccessors = React.useMemo<SortAccessors<TransactionSummary, UnmappedSortKey>>(
    () => ({
      date: (t) => t.businessDate,
      account: (t) => accountById.get(t.accountId)?.name ?? t.accountId,
      direction: (t) => t.direction,
      amount: (t) => t.amountPaise,
      category: (t) => t.categoryName,
      description: (t) => t.description,
    }),
    [accountById],
  );
  const { rows, ...sorter } = useSortedRows(listQuery.data?.items ?? EMPTY_UNMAPPED, sortAccessors);

  const bulkMapMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!overheadId) throw new ApiRequestError(400, 'no_overhead', 'No default overhead cost center found.');
      for (const id of ids) {
        await api.mapTransaction(id, { costCenterId: overheadId });
      }
    },
    onSuccess: async () => {
      setSelected(new Set());
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['unmapped'] });
      await queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Unmapped queue</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Transactions still waiting on a project or cost-center mapping. Sink boring ones into Overhead.
          </p>
        </div>
        <Button
          disabled={selected.size === 0 || !overheadId || bulkMapMutation.isPending}
          onClick={() => bulkMapMutation.mutate(Array.from(selected))}
        >
          {bulkMapMutation.isPending ? 'Mapping…' : `Map ${selected.size} to Overhead`}
        </Button>
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Unmapped transactions</CardTitle>
          <CardDescription>{rows.length} shown</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Nothing to map — clean queue.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-[hsl(var(--muted-foreground))]">
                  <tr className="text-left">
                    <th className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={selected.size === rows.length && rows.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <SortableTh sortKey="date" controller={sorter} className="py-2 pr-3">
                      Date
                    </SortableTh>
                    <SortableTh sortKey="account" controller={sorter} className="py-2 pr-3">
                      Account
                    </SortableTh>
                    <SortableTh sortKey="direction" controller={sorter} className="py-2 pr-3">
                      Dir
                    </SortableTh>
                    <SortableTh sortKey="amount" controller={sorter} align="right" className="py-2 pr-3">
                      Amount
                    </SortableTh>
                    <SortableTh sortKey="category" controller={sorter} className="py-2 pr-3">
                      Category
                    </SortableTh>
                    <SortableTh sortKey="description" controller={sorter} className="py-2 pr-3">
                      Description
                    </SortableTh>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="border-t border-[hsl(var(--border))]">
                      <td className="py-2 pr-3">
                        <input
                          type="checkbox"
                          checked={selected.has(t.id)}
                          onChange={() => toggle(t.id)}
                        />
                      </td>
                      <td className="py-2 pr-3">{t.businessDate}</td>
                      <td className="py-2 pr-3">{accountById.get(t.accountId)?.name ?? t.accountId}</td>
                      <td className={`py-2 pr-3 font-medium ${t.direction === 'in' ? 'text-green-700' : 'text-red-700'}`}>
                        {t.direction}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(t.amountPaise))}</td>
                      <td className="py-2 pr-3">{t.categoryName ?? '—'}</td>
                      <td className="py-2 pr-3">{t.description}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="destructive">Unmapped</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
